import { useNavigate } from "react-router-dom";
import CourseCard from "../component/AllCoursePageComponent/CourseCard";
import FilterSidebar from "../component/AllCoursePageComponent/FilterSideBar";
import Layout from "../component/layout/Layout";
import Loader from "../component/Loader/Loader";
import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import { getAllPublicCourses, searchCourse } from "../helper/course_helper";
import { generatePresignedUrlForAccess } from "../helper/content_helper";
import { FaSearch, FaTimes, FaChevronDown } from 'react-icons/fa';
import { HiAdjustmentsHorizontal, HiMagnifyingGlass } from 'react-icons/hi2';
import { BsGrid3X3GapFill, BsListUl } from 'react-icons/bs';

const AllCoursePage = () => {
    // Store all courses (original data with correct mrp values)
    const [allCourses, setAllCourses] = useState([]);
    // Store filtered courses for display
    const [displayedCourses, setDisplayedCourses] = useState([]);
    const [loader, setLoader] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('newest');
    const [showSortDropdown, setShowSortDropdown] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [debounceTimeout, setDebounceTimeout] = useState(null);

    // Store current filters
    const [activeFilters, setActiveFilters] = useState({
        categoryIds: [],
        subCategoryIds: [],
        levelIds: [],
        durations: [],
        priceRange: [0, 10000],
        showFreeOnly: false
    });

    // Check screen size
    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth < 992);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const fetchCourses = async () => {
        try {
            setLoader(true);
            const data = await getAllPublicCourses();
            if (data?.success) {
                const coursesWithThumbnails = await updateThumbnails(data?.result);
                setAllCourses(coursesWithThumbnails);
                setDisplayedCourses(sortCourses(coursesWithThumbnails, sortBy));
            } else {
                toast.error(data?.message);
            }
        } catch (error) {
            toast.error(error?.response?.data?.message || "Something went wrong");
        } finally {
            setLoader(false);
        }
    };

    const updateThumbnails = async (data) => {
        const updateData = await Promise.all(
            data.map(async (d) => {
                if (d?.thumbnailName) {
                    let name = d.thumbnailName;
                    let type = "image";
                    const response = await generatePresignedUrlForAccess({ name, type });
                    if (response.success) {
                        return { ...d, thumbnailUrl: response.presignedUrl };
                    } else {
                        console.error('Error generating pre-signed URL for', d.thumbnailName);
                        return d;
                    }
                }
                return d;
            })
        );
        return updateData;
    };

    const sortCourses = (courses, sortType) => {
        const sorted = [...courses];
        switch (sortType) {
            case 'price-low':
                return sorted.sort((a, b) => (a.price || 0) - (b.price || 0));
            case 'price-high':
                return sorted.sort((a, b) => (b.price || 0) - (a.price || 0));
            case 'newest':
            default:
                return sorted;
        }
    };

    // Apply filters to the original course data (client-side)
    const applyFilters = useCallback((filters) => {
        setActiveFilters(filters);

        let filtered = [...allCourses];

        // Filter by category
        if (filters.categoryIds && filters.categoryIds.length > 0) {
            filtered = filtered.filter(course => {
                const courseCategoryId = course?.subCategoryDto?.categoryDto?.id;
                return filters.categoryIds.includes(courseCategoryId);
            });
        }

        // Filter by sub-category
        if (filters.subCategoryIds && filters.subCategoryIds.length > 0) {
            filtered = filtered.filter(course => {
                const courseSubCategoryId = course?.subCategoryDto?.id || course?.subCategoryId;
                return filters.subCategoryIds.includes(courseSubCategoryId);
            });
        }

        // Filter by level
        if (filters.levelIds && filters.levelIds.length > 0) {
            filtered = filtered.filter(course => {
                const courseLevel = course?.level || course?.levelName;
                return filters.levelIds.includes(courseLevel);
            });
        }

        // Filter by duration
        if (filters.durations && filters.durations.length > 0) {
            filtered = filtered.filter(course => {
                const duration = course?.duration || 0;
                return filters.durations.some(d => {
                    if (d === '1 - 3 Hours') return duration >= 1 && duration <= 3;
                    if (d === '1 - 5 Hours') return duration >= 1 && duration <= 5;
                    if (d === '1 - 10 Hours') return duration >= 1 && duration <= 10;
                    if (d === '1 - 20 Hours') return duration >= 1 && duration <= 20;
                    if (d === '1 - 50 Hours') return duration >= 1 && duration <= 50;
                    if (d === 'More than 50 Hours') return duration > 50;
                    return false;
                });
            });
        }

        // Filter by price range
        if (filters.priceRange) {
            const [minPrice, maxPrice] = filters.priceRange;
            if (minPrice > 0 || maxPrice < 10000) {
                filtered = filtered.filter(course => {
                    const coursePrice = course?.price || 0;
                    const minOk = coursePrice >= minPrice;
                    const maxOk = maxPrice >= 10000 || coursePrice <= maxPrice;
                    return minOk && maxOk;
                });
            }
        }

        // Filter free courses only
        if (filters.showFreeOnly) {
            filtered = filtered.filter(course => {
                return course?.price === 0 || course?.price === null;
            });
        }

        // Apply sorting and update display
        const sorted = sortCourses(filtered, sortBy);
        setDisplayedCourses(sorted);
    }, [allCourses, sortBy]);

    // Function passed to FilterSidebar
    const handleFilterChange = (filters) => {
        setLoader(true);
        try {
            applyFilters(filters);
        } finally {
            setLoader(false);
        }
    };

    // Reset filters and show all courses
    const handleClearFilters = () => {
        setActiveFilters({
            categoryIds: [],
            subCategoryIds: [],
            levelIds: [],
            durations: [],
            priceRange: [0, 10000],
            showFreeOnly: false
        });
        setDisplayedCourses(sortCourses(allCourses, sortBy));
    };

    const handleSortChange = (newSort) => {
        setSortBy(newSort);
        setShowSortDropdown(false);
        const sorted = sortCourses(displayedCourses, newSort);
        setDisplayedCourses(sorted);
    };

    const handleSearch = useCallback((query) => {
        setSearchQuery(query);
        if (debounceTimeout) clearTimeout(debounceTimeout);

        const timeout = setTimeout(async () => {
            if (query.length > 2) {
                try {
                    setLoader(true);
                    const data = await searchCourse(query);
                    if (data?.success) {
                        const coursesWithThumbnails = await updateThumbnails(data?.result);
                        // Store search results but preserve for filtering
                        setAllCourses(coursesWithThumbnails);
                        setDisplayedCourses(sortCourses(coursesWithThumbnails, sortBy));
                    } else {
                        toast.error(data?.message);
                    }
                } catch (error) {
                    console.log(error?.response?.data?.message || "Something went wrong");
                } finally {
                    setLoader(false);
                }
            } else if (query.length === 0) {
                fetchCourses();
            }
        }, 400);
        setDebounceTimeout(timeout);
    }, [debounceTimeout, sortBy]);

    const clearSearch = () => {
        setSearchQuery('');
        fetchCourses();
    };

    useEffect(() => {
        fetchCourses();
    }, []);

    const getSortLabel = () => {
        switch (sortBy) {
            case 'price-low': return 'Price: Low to High';
            case 'price-high': return 'Price: High to Low';
            default: return 'Newest First';
        }
    };

    return (
        <>
            {loader && <Loader />}
            <Layout tab={2}>
                <div style={styles.pageContainer}>
                    {/* Hero Section */}
                    <div style={styles.heroSection}>
                        <div style={styles.heroContent}>
                            <h1 style={styles.heroTitle}>Explore Our Courses</h1>
                            <p style={styles.heroSubtitle}>
                                Discover industry-relevant skills with our curated courses
                            </p>

                            {/* Search Bar */}
                            <div style={styles.searchContainer}>
                                <HiMagnifyingGlass style={styles.searchIcon} />
                                <input
                                    type="text"
                                    placeholder="Search for courses, topics, or skills..."
                                    style={styles.searchInput}
                                    value={searchQuery}
                                    onChange={(e) => handleSearch(e.target.value)}
                                />
                                {searchQuery && (
                                    <button style={styles.clearSearchBtn} onClick={clearSearch}>
                                        <FaTimes size={14} />
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Main Content */}
                    <div style={styles.contentWrapper}>
                        {/* Sidebar - Desktop */}
                        {!isMobile && (
                            <div style={styles.sidebarColumn}>
                                <FilterSidebar
                                    onFilterChange={handleFilterChange}
                                    onClearFilters={handleClearFilters}
                                    setLoader={setLoader}
                                />
                            </div>
                        )}

                        {/* Courses Section */}
                        <div style={styles.coursesColumn}>
                            {/* Toolbar */}
                            <div style={styles.toolbar}>
                                <div style={styles.toolbarLeft}>
                                    {/* Mobile Filter Button */}
                                    {isMobile && (
                                        <FilterSidebar
                                            onFilterChange={handleFilterChange}
                                            onClearFilters={handleClearFilters}
                                            setLoader={setLoader}
                                        />
                                    )}
                                    <span style={styles.courseCount}>
                                        <strong>{displayedCourses.length}</strong> courses found
                                    </span>
                                </div>

                                {/* Sort Dropdown */}
                                <div style={styles.sortContainer}>
                                    <span style={styles.sortLabel}>Sort by:</span>
                                    <div style={styles.sortDropdown}>
                                        <button
                                            style={styles.sortButton}
                                            onClick={() => setShowSortDropdown(!showSortDropdown)}
                                        >
                                            <span>{getSortLabel()}</span>
                                            <FaChevronDown size={12} style={{
                                                transition: 'transform 0.2s',
                                                transform: showSortDropdown ? 'rotate(180deg)' : 'rotate(0)'
                                            }} />
                                        </button>
                                        {showSortDropdown && (
                                            <div style={styles.dropdownMenu}>
                                                <button
                                                    style={{
                                                        ...styles.dropdownItem,
                                                        ...(sortBy === 'newest' ? styles.dropdownItemActive : {})
                                                    }}
                                                    onClick={() => handleSortChange('newest')}
                                                >
                                                    Newest First
                                                </button>
                                                <button
                                                    style={{
                                                        ...styles.dropdownItem,
                                                        ...(sortBy === 'price-low' ? styles.dropdownItemActive : {})
                                                    }}
                                                    onClick={() => handleSortChange('price-low')}
                                                >
                                                    Price: Low to High
                                                </button>
                                                <button
                                                    style={{
                                                        ...styles.dropdownItem,
                                                        ...(sortBy === 'price-high' ? styles.dropdownItemActive : {})
                                                    }}
                                                    onClick={() => handleSortChange('price-high')}
                                                >
                                                    Price: High to Low
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Courses Grid */}
                            <div style={styles.coursesGrid}>
                                {displayedCourses && displayedCourses.length > 0 ? (
                                    displayedCourses.map((course, index) => (
                                        <div key={course.id || index} style={styles.courseCardWrapper}>
                                            <CourseCard course={course} index={index} />
                                        </div>
                                    ))
                                ) : (
                                    <div style={styles.emptyState}>
                                        <div style={styles.emptyIcon}>📚</div>
                                        <h3 style={styles.emptyTitle}>No courses found</h3>
                                        <p style={styles.emptyText}>
                                            Try adjusting your filters or search query
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </Layout>
        </>
    );
};

const styles = {
    pageContainer: {
        backgroundColor: "#f8fafc",
        minHeight: "100vh",
    },
    // Hero Section
    heroSection: {
        background: 'linear-gradient(135deg, #024076 0%, #0b5394 50%, #024076 100%)',
        padding: '60px 24px 50px',
        position: 'relative',
        overflow: 'hidden',
    },
    heroContent: {
        maxWidth: '800px',
        margin: '0 auto',
        textAlign: 'center',
        position: 'relative',
        zIndex: 1,
    },
    heroTitle: {
        fontSize: 'clamp(28px, 5vw, 42px)',
        fontWeight: '800',
        color: '#ffffff',
        margin: '0 0 12px 0',
        lineHeight: 1.2,
        fontFamily: "'Roboto Slab', serif",
    },
    heroSubtitle: {
        fontSize: 'clamp(14px, 2vw, 18px)',
        color: 'rgba(255, 255, 255, 0.9)',
        margin: '0 0 32px 0',
        fontWeight: '400',
    },
    searchContainer: {
        display: 'flex',
        alignItems: 'center',
        maxWidth: '600px',
        margin: '0 auto',
        backgroundColor: '#ffffff',
        borderRadius: '14px',
        padding: '6px 8px 6px 20px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
    },
    searchIcon: {
        fontSize: '20px',
        color: '#94a3b8',
        marginRight: '12px',
        flexShrink: 0,
    },
    searchInput: {
        flex: 1,
        border: 'none',
        outline: 'none',
        fontSize: '15px',
        color: '#1e293b',
        backgroundColor: 'transparent',
        padding: '12px 0',
        fontWeight: '500',
    },
    clearSearchBtn: {
        background: '#f1f5f9',
        border: 'none',
        borderRadius: '8px',
        padding: '10px',
        cursor: 'pointer',
        color: '#64748b',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginLeft: '8px',
    },
    // Main Content
    contentWrapper: {
        display: 'flex',
        gap: '28px',
        padding: '28px 24px',
        maxWidth: '1500px',
        margin: '0 auto',
    },
    sidebarColumn: {
        flex: '0 0 300px',
        position: 'sticky',
        top: '100px',
        alignSelf: 'flex-start',
        maxHeight: 'calc(100vh - 120px)',
    },
    coursesColumn: {
        flex: '1',
        minWidth: 0,
    },
    // Toolbar
    toolbar: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '16px',
    },
    toolbarLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
    },
    courseCount: {
        fontSize: '14px',
        color: '#64748b',
    },
    sortContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
    },
    sortLabel: {
        fontSize: '14px',
        color: '#64748b',
        fontWeight: '500',
    },
    sortDropdown: {
        position: 'relative',
    },
    sortButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        backgroundColor: '#ffffff',
        border: '1px solid #e5e7eb',
        borderRadius: '10px',
        padding: '10px 16px',
        fontSize: '14px',
        fontWeight: '600',
        color: '#1e293b',
        cursor: 'pointer',
        transition: 'all 0.2s',
    },
    dropdownMenu: {
        position: 'absolute',
        top: '100%',
        right: 0,
        marginTop: '8px',
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.12)',
        border: '1px solid #e5e7eb',
        overflow: 'hidden',
        zIndex: 100,
        minWidth: '180px',
    },
    dropdownItem: {
        display: 'block',
        width: '100%',
        padding: '12px 16px',
        fontSize: '14px',
        fontWeight: '500',
        color: '#475569',
        backgroundColor: 'transparent',
        border: 'none',
        textAlign: 'left',
        cursor: 'pointer',
        transition: 'background-color 0.2s',
    },
    dropdownItemActive: {
        backgroundColor: '#f0f9ff',
        color: '#024076',
        fontWeight: '600',
    },
    // Courses Grid
    coursesGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
        gap: '24px',
    },
    courseCardWrapper: {
        width: '100%',
    },
    // Empty State
    emptyState: {
        gridColumn: '1 / -1',
        textAlign: 'center',
        padding: '80px 20px',
        backgroundColor: '#ffffff',
        borderRadius: '16px',
        border: '1px solid #e5e7eb',
    },
    emptyIcon: {
        fontSize: '64px',
        marginBottom: '20px',
    },
    emptyTitle: {
        fontSize: '22px',
        fontWeight: '700',
        color: '#1e293b',
        margin: '0 0 12px 0',
    },
    emptyText: {
        fontSize: '15px',
        color: '#64748b',
        margin: 0,
    },
};

// Add global styles for responsive design
const responsiveStyles = `
    @media (max-width: 991px) {
        .page-container {
            padding-top: 0 !important;
        }
    }
    
    @media (max-width: 768px) {
        .courses-grid {
            grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)) !important;
            gap: 16px !important;
        }
    }
    
    @media (max-width: 480px) {
        .courses-grid {
            grid-template-columns: 1fr !important;
        }
    }
    
    /* Custom range slider styling */
    input[type="range"] {
        -webkit-appearance: none;
        appearance: none;
        background: transparent;
    }
    
    input[type="range"]::-webkit-slider-thumb {
        -webkit-appearance: none;
        appearance: none;
        width: 18px;
        height: 18px;
        border-radius: 50%;
        background: #024076;
        cursor: pointer;
        border: 3px solid #ffffff;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
        margin-top: -6px;
    }
    
    input[type="range"]::-moz-range-thumb {
        width: 18px;
        height: 18px;
        border-radius: 50%;
        background: #024076;
        cursor: pointer;
        border: 3px solid #ffffff;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
    }
`;

// Inject styles
if (typeof document !== 'undefined') {
    const styleSheet = document.createElement('style');
    styleSheet.textContent = responsiveStyles;
    document.head.appendChild(styleSheet);
}

export default AllCoursePage;